import { useTranslation } from "react-i18next";

import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

/**
 * Roughly the point at which an adenda stops fitting the box above the CAE band
 * and starts adding a page: the renderer wraps at ~95 characters per line at
 * 9pt, and about 11 lines fit before the box reaches the footer.
 *
 * ADVISORY ONLY — nothing truncates at this number. It exists so a user writing
 * a long adenda is told the real consequence (another page) instead of being
 * stopped at an invented limit DGI does not impose.
 */
export const ADENDA_SPILLS_TO_ANOTHER_PAGE_AT = 1000;

/** Whether `text` is long enough to push the adenda box onto another page. */
export function adendaSpillsToAnotherPage(text: string): boolean {
  return text.trim().length > ADENDA_SPILLS_TO_ANOTHER_PAGE_AT;
}

export interface AdendaFieldProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  /** Omit (or pass "") where a surrounding heading already names the field —
   *  an empty <Label> still renders and leaves a stray gap above the box. */
  label?: string;
  /** Shown under the field. Each surface explains its own scope. */
  helper: string;
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
}

/**
 * The adenda editor (CFE Zona J, Res. DGI 1264/023), shared by all three
 * surfaces that author one: the company's standing adenda, a marca's override,
 * and the per-invoice addition.
 *
 * One component because the three differ only in label and helper text — and
 * because the character counter below has to agree everywhere. A copy per
 * surface is how one of them ends up with a cap the others do not have.
 *
 * NO MAXLENGTH, deliberately. DGI declares `Adenda` as untyped with no length
 * facet, so there is nothing to mirror, and the PDF renderer paginates a long
 * adenda rather than overflowing. The counter is informational: it tells a user
 * writing a long adenda that it will add pages, which is the real consequence,
 * instead of silently truncating their text at an invented limit.
 */
export function AdendaField({
  id,
  value,
  onChange,
  label,
  helper,
  placeholder,
  disabled,
  rows = 4,
}: AdendaFieldProps): JSX.Element {
  const { t } = useTranslation();
  const characters = value.trim().length;
  const spillsToAnotherPage = adendaSpillsToAnotherPage(value);

  return (
    <div className="space-y-2">
      {label ? (
        <Label htmlFor={id} className="text-foreground">
          {label}
        </Label>
      ) : null}
      <Textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        className="transition-colors duration-150"
      />
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-muted-foreground">{helper}</p>
        {characters > 0 && (
          <p
            className={
              spillsToAnotherPage
                ? "shrink-0 text-sm text-amber-600"
                : "shrink-0 text-sm text-muted-foreground"
            }
          >
            {spillsToAnotherPage
              ? t(
                  "adenda.charactersLong",
                  "{{count}} caracteres · puede agregar una página",
                  { count: characters }
                )
              : t("adenda.characters", "{{count}} caracteres", {
                  count: characters,
                })}
          </p>
        )}
      </div>
    </div>
  );
}
